import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KingBedOutlinedIcon from '@mui/icons-material/KingBedOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import '../Reservations/Reservations.css';

interface IBookingForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  adults: number;
  children: number;
  specialRequests?: string;
}

interface Props {
  hotelId: number | null;
  room: any;
  checkInDate: string;
  checkOutDate: string;
  onClose: () => void;
}

type RateOption = {
  ratePlanId: number;
  ratePlanName: string;
  totalPrice: number;
};

const BookingPublic = ({ hotelId, room, checkInDate, checkOutDate, onClose }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IBookingForm>({
    defaultValues: {
      adults: 1,
      children: 0,
      specialRequests: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const rateOptions = useMemo<RateOption[]>(() => {
    const options = Array.isArray(room?.rateOptions) ? room.rateOptions : [];

    return options
      .map((option: any): RateOption => ({
        ratePlanId: Number(option?.ratePlanId ?? option?.RatePlanId ?? 0),
        ratePlanName: option?.ratePlanName ?? option?.RatePlanName ?? '',
        totalPrice: Number(option?.totalPrice ?? option?.TotalPrice ?? 0),
      }))
      .filter((option: RateOption) => option.ratePlanId > 0);
  }, [room]);
  const defaultRatePlanId = useMemo(() => {
    const recommendedRatePlanId = Number(room?.recommendedRatePlanId ?? room?.RecommendedRatePlanId ?? 0);

    if (recommendedRatePlanId > 0 && rateOptions.some((option) => option.ratePlanId === recommendedRatePlanId)) {
      return String(recommendedRatePlanId);
    }

    return rateOptions[0]?.ratePlanId ? String(rateOptions[0].ratePlanId) : '';
  }, [rateOptions, room]);
  const [selectedRatePlanId, setSelectedRatePlanId] = useState(defaultRatePlanId);

  const selectedRateOption = useMemo(
    () => rateOptions.find((option) => String(option.ratePlanId) === selectedRatePlanId) ?? null,
    [rateOptions, selectedRatePlanId]
  );

  const stayNights = useMemo(() => {
    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

    const diff = end.getTime() - start.getTime();
    return diff > 0 ? Math.ceil(diff / (1000 * 60 * 60 * 24)) : 0;
  }, [checkInDate, checkOutDate]);

  const formattedStay = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Dates to be confirmed';

    return `${formatter.format(start)} - ${formatter.format(end)}`;
  }, [checkInDate, checkOutDate]);

  const estimatedTotal = useMemo(() => {
    const totalFromRate = selectedRateOption?.totalPrice;
    if (typeof totalFromRate === 'number' && totalFromRate > 0) return totalFromRate;

    const roomTotal = Number(room?.totalPrice ?? 0);
    if (roomTotal > 0) return roomTotal;

    const perNight = Number(room?.pricePerNight ?? 0);
    if (perNight > 0 && stayNights > 0) return perNight * stayNights;

    return 0;
  }, [room, selectedRateOption, stayNights]);

  const formatMoney = (value: number) =>
    new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);

  useEffect(() => {
    setSelectedRatePlanId(defaultRatePlanId);
  }, [defaultRatePlanId]);

  const payload = useMemo(
    () => ({
      hotelId,
      guestFirstName: '',
      guestLastName: '',
      guestEmail: '',
      guestPhone: '',
      requestedRoomTypeId: room?.roomTypeId,
      requestedRatePlanId: selectedRatePlanId ? Number(selectedRatePlanId) : null,
      checkInDate,
      checkOutDate,
    }),
    [checkInDate, checkOutDate, hotelId, room, selectedRatePlanId]
  );

  const onSubmit = async (data: IBookingForm) => {
    const normalizedAdults = Number(data.adults);
    const normalizedChildren = Number(data.children ?? 0);
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);

    if (!hotelId) {
      toast.error('Hotel is required.');
      return;
    }

    if (!room?.roomTypeId) {
      toast.error('Requested room type is required.');
      return;
    }

    if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime()) || checkIn >= checkOut) {
      toast.error('Check-in date must be before check-out date.');
      return;
    }

    if (!normalizedAdults || normalizedAdults <= 0) {
      toast.error('Adults must be greater than 0.');
      return;
    }

    try {
      setLoading(true);

      await axiosInstance.post(
        ApiConstants.bookingRequest.bookingRequestCreatePublic,
        {
          ...payload,
          guestFirstName: data.firstName.trim(),
          guestLastName: data.lastName.trim(),
          guestEmail: data.email.trim(),
          guestPhone: data.phone.trim(),
          adults: normalizedAdults,
          children: normalizedChildren >= 0 ? normalizedChildren : 0,
          specialRequests: data.specialRequests?.trim() || null,
        }
      );

      toast.success('Booking request sent.');
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data || err?.message || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="hotelForm booking-public" onSubmit={handleSubmit(onSubmit)}>
      <div className="booking-public-shell">
        <section className="booking-public-hero">
          <div className="booking-public-hero-copy">
            <span className="booking-public-badge">
              <AutoAwesomeOutlinedIcon fontSize="inherit" />
              Refined booking request
            </span>

            <h2>Reserve your stay with a cleaner, premium flow.</h2>
            <p>
              Fill in your guest details, review your stay, and send a polished request that feels aligned with the hotel
              experience.
            </p>

            <div className="booking-public-highlight-grid">
              <div className="booking-public-highlight-card">
                <span className="booking-public-highlight-icon">
                  <KingBedOutlinedIcon fontSize="inherit" />
                </span>
                <div>
                  <strong>{room?.roomTypeName || 'Selected room'}</strong>
                  <small>{room?.roomNumber ? `Room ${room.roomNumber}` : 'Room type confirmed'}</small>
                </div>
              </div>

              <div className="booking-public-highlight-card">
                <span className="booking-public-highlight-icon">
                  <CalendarMonthOutlinedIcon fontSize="inherit" />
                </span>
                <div>
                  <strong>{stayNights > 0 ? `${stayNights} ${stayNights === 1 ? 'night' : 'nights'}` : 'Flexible stay'}</strong>
                  <small>{formattedStay}</small>
                </div>
              </div>
            </div>
          </div>

          <aside className="booking-public-summary-card">
            <p className="booking-public-summary-label">Booking snapshot</p>
            <div className="booking-public-summary-price">
              <span>Estimated total</span>
              <strong>{estimatedTotal > 0 ? `EUR ${formatMoney(estimatedTotal)}` : 'On request'}</strong>
            </div>

            <div className="booking-public-summary-grid">
              <div className="booking-public-summary-item">
                <span>Stay</span>
                <strong>{formattedStay}</strong>
              </div>

              <div className="booking-public-summary-item">
                <span>Room</span>
                <strong>{room?.roomTypeName || 'Selected room'}</strong>
              </div>

              <div className="booking-public-summary-item">
                <span>Rate</span>
                <strong>{selectedRateOption?.ratePlanName || room?.recommendedRatePlanName || 'Best available'}</strong>
              </div>

              <div className="booking-public-summary-item">
                <span>Status</span>
                <strong>Request pending approval</strong>
              </div>
            </div>
          </aside>
        </section>

        <section className="booking-public-form-card">
          <div className="booking-public-section-head">
            <div>
              <p className="booking-public-eyebrow">Guest details</p>
              <h3>Personalize your booking request</h3>
            </div>
            <div className="booking-public-inline-note">
              <PersonOutlineOutlinedIcon fontSize="inherit" />
              We will use these details to contact you about confirmation.
            </div>
          </div>

          <div className="booking-public-grid">
            <div className="giWrap booking-public-field">
              <label htmlFor="firstName" className={`giLabel ${errors.firstName ? 'isError' : ''}`}>
                First Name
              </label>
              <input
                id="firstName"
                className={`giInput ${errors.firstName ? 'isError' : ''}`}
                placeholder="Enter first name"
                {...register('firstName', { required: true })}
              />
              {errors.firstName && <p className="giErrorText">Guest first name is required.</p>}
            </div>

            <div className="giWrap booking-public-field">
              <label htmlFor="lastName" className={`giLabel ${errors.lastName ? 'isError' : ''}`}>
                Last Name
              </label>
              <input
                id="lastName"
                className={`giInput ${errors.lastName ? 'isError' : ''}`}
                placeholder="Enter last name"
                {...register('lastName', { required: true })}
              />
              {errors.lastName && <p className="giErrorText">Guest last name is required.</p>}
            </div>

            <div className="giWrap booking-public-field">
              <label htmlFor="email" className={`giLabel ${errors.email ? 'isError' : ''}`}>
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`giInput ${errors.email ? 'isError' : ''}`}
                placeholder="Enter email"
                {...register('email', { required: true })}
              />
              {errors.email && <p className="giErrorText">Guest email is required.</p>}
            </div>

            <div className="giWrap booking-public-field">
              <label htmlFor="phone" className={`giLabel ${errors.phone ? 'isError' : ''}`}>
                Phone
              </label>
              <input
                id="phone"
                className={`giInput ${errors.phone ? 'isError' : ''}`}
                placeholder="Enter phone"
                {...register('phone', { required: true })}
              />
              {errors.phone && <p className="giErrorText">Guest phone is required.</p>}
            </div>

            <div className="giWrap booking-public-field">
              <label htmlFor="ratePlan" className="giLabel">
                Rate Plan
              </label>
              <select
                id="ratePlan"
                className="giInput"
                value={selectedRatePlanId}
                onChange={(e) => setSelectedRatePlanId(e.target.value)}
                disabled={rateOptions.length === 0}
              >
                {rateOptions.length === 0 && <option value="">Auto</option>}
                {rateOptions.map((option) => (
                  <option key={option.ratePlanId} value={option.ratePlanId}>
                    {option.ratePlanName} - EUR {formatMoney(option.totalPrice)}
                  </option>
                ))}
              </select>
            </div>

            <div className="booking-public-guest-grid">
              <div className="giWrap booking-public-field">
                <label htmlFor="adults" className={`giLabel ${errors.adults ? 'isError' : ''}`}>
                  Adults
                </label>
                <input
                  id="adults"
                  type="number"
                  min={1}
                  className={`giInput ${errors.adults ? 'isError' : ''}`}
                  placeholder="Adults"
                  {...register('adults', {
                    required: true,
                    valueAsNumber: true,
                    min: 1,
                  })}
                />
                {errors.adults && <p className="giErrorText">Adults must be greater than 0.</p>}
              </div>

              <div className="giWrap booking-public-field">
                <label htmlFor="children" className={`giLabel ${errors.children ? 'isError' : ''}`}>
                  Children
                </label>
                <input
                  id="children"
                  type="number"
                  min={0}
                  className={`giInput ${errors.children ? 'isError' : ''}`}
                  placeholder="Children"
                  {...register('children', {
                    valueAsNumber: true,
                    min: 0,
                  })}
                />
                {errors.children && <p className="giErrorText">Children cannot be negative.</p>}
              </div>
            </div>
          </div>

          <div className="booking-public-request-card">
            <div className="booking-public-request-head">
              <p className="booking-public-eyebrow">Special touch</p>
              <div className="booking-public-inline-note">
                <LocalOfferOutlinedIcon fontSize="inherit" />
                Share arrival notes, bed setup, or anything the team should know.
              </div>
            </div>

            <div className="giWrap booking-public-field">
              <label htmlFor="specialRequests" className="giLabel">
                Special Requests
              </label>
              <textarea
                id="specialRequests"
                className="giInput booking-public-textarea"
                placeholder="Add any request that would make your stay smoother"
                rows={4}
                {...register('specialRequests')}
              />
            </div>
          </div>

          <div className="hotelActions booking-public-actions">
            <Button
              variant="outlined"
              onClick={onClose}
              className="booking-public-btn booking-public-btn-secondary"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              className="booking-public-btn booking-public-btn-primary"
            >
              {loading ? 'Sending...' : 'Confirm Booking'}
            </Button>
          </div>
        </section>
      </div>
    </form>
  );
};

export default BookingPublic;
